// src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { BuyerProvider, useBuyer } from "./contexts/BuyerContext";
import { ProductProvider } from "./contexts/ProductContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SafeAreaProvider } from "./utils/SafeAreaProvider";
import { AlertProvider } from "./contexts/AlertContext";
import { NavigationBarManager } from "./utils/NavigationBarManager";
import "./styles/AppLayout.css";
// All your pages...
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./components/Dashboard";
import BuyerRegistration from "./pages/BuyerRegsitartion";
import ProductSegment from "./pages/ProductSegment";
import Catalog from "./components/Catlouge";
import ProductDetails from "./components/ProductDetails";
import OrdersFlow from "./pages/OrderFlow";
import Selections from "./pages/Selections";
import Orders from "./pages/Order";
import OrdersinProgress from "./pages/OrdersinProgress";
import OrdersDelivered from "./pages/orderDelivery/OrdersDelivered";
import OrderHistory from "./pages/orderDelivery/OrderHistory";
import Prepartion from "./pages/Prepartion";
import Processing from "./pages/Processing";
import Profile from "./pages/Profile";
import LiveRates from "./pages/LiveRates";
import ForgetPassword from "./pages/ForgetPassword";
import OpenMarketUploadProductRequest from "./pages/openmarket/OpenMarketUploadProductRequest";
import OpenMarketFinal from "./pages/openmarket/OpenMarketFinal";
import OpenMarketStatusPage from "./pages/openmarket/OpenMarketStatusPage";
import LikedProducts from "./pages/LikedProdcut";
import Settings from "./pages/Settings";
import VisualDesignerStudio from "./pages/VisualDesignerStudio";
import RewardsPage from "./pages/RewardsPage";

import { SplashSkeleton } from "./components/Skeleton";

// ─── Status Bar Controller ──────────────────────────────────────────────────
// Listens to every route change and updates the native status bar color + icon
// style via NavigationBarManager.setForPage().
// Rules (from spec):
//   /dashboard  → Gold #D4AF37, dark style (white icons on gold)
//   All others  → White #FFFFFF, light style (dark icons on white)
//   Dark mode   → #121212 on all routes
const StatusBarController = () => {
  const location = useLocation();

  useEffect(() => {
    // Fire-and-forget — no need to await in render cycle
    NavigationBarManager.setForPage(location.pathname);
  }, [location.pathname]);

  return null; // Renders nothing; side-effect only
};

// 🔒 Protected Route (requires buyerId)
const ProtectedRoute = ({ children }) => {
  const { buyer, loading } = useBuyer();

  if (loading) return <SplashSkeleton />;
  if (!buyer?.buyerId) return <Navigate to="/login" replace />;

  return children;
};

// 🚪 Public Route (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { buyer, loading } = useBuyer();

  if (loading) return <SplashSkeleton />;
  if (buyer?.buyerId && buyer?.registrationStatus)
    return <Navigate to="/dashboard" replace />;

  return children;
};

const AppLayout = ({ children }) => {
  return (
    <div className="app-layout">
      <div className="app-content">{children}</div>
    </div>
  );
};

// 🧪 Wrapper to handle initial app state
const AppContent = () => {
  const { buyer, loading } = useBuyer();

  useEffect(() => {
    // Auth state managed by BuyerContext
  }, [loading, buyer]);

  if (loading) {
    return <SplashSkeleton />;
  }

  return (
    <Routes>
      {/* Redirect root based on auth */}
      <Route
        path="/"
        element={
          <Navigate to={buyer?.buyerId ? "/dashboard" : "/login"} replace />
        }
      />

      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgetpassword"
        element={
          <PublicRoute>
            <ForgetPassword />
          </PublicRoute>
        }
      />
      <Route path="/favorite" element={<LikedProducts />} />
      {/* Protected routes */}
      <Route
        path="/liverates"
        element={
          <ProtectedRoute>
            <LiveRates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/selections"
        element={
          <ProtectedRoute>
            <Selections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/productSegment/:segment"
        element={
          <ProtectedRoute>
            <ProductSegment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/productSegment/:segment/:purity"
        element={
          <ProtectedRoute>
            <ProductSegment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/productSegment/:segment/:purity/:category"
        element={
          <ProtectedRoute>
            <Catalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/product/:productId"
        element={
          <ProtectedRoute>
            <ProductDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orderflow"
        element={
          <ProtectedRoute>
            <OrdersFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:type"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/requested"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ordersinprogress"
        element={
          <ProtectedRoute>
            <OrdersinProgress />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ordersdelivered"
        element={
          <ProtectedRoute>
            <OrdersDelivered />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prepartion"
        element={
          <ProtectedRoute>
            <Prepartion />
          </ProtectedRoute>
        }
      />
      <Route
        path="/processing"
        element={
          <ProtectedRoute>
            <Processing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orderhistory"
        element={
          <ProtectedRoute>
            <OrderHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyerregistration"
        element={
          <ProtectedRoute>
            <BuyerRegistration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <RewardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visual-designer/:segment"
        element={
          <ProtectedRoute>
            <VisualDesignerStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/openmarket/request"
        element={
          <ProtectedRoute>
            <OpenMarketUploadProductRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/openmarket/final"
        element={
          <ProtectedRoute>
            <OpenMarketFinal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/openmarket/:type"
        element={
          <ProtectedRoute>
            <OpenMarketStatusPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route
        path="*"
        element={
          <Navigate to={buyer?.buyerId ? "/dashboard" : "/login"} replace />
        }
      />
    </Routes>
  );
};

// 🌐 Main App
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BuyerProvider>
          <ProductProvider>
            <SafeAreaProvider>
              <AlertProvider>
                <Router>
                  {/* Route-aware status bar switcher — must be inside Router */}
                  <StatusBarController />
                  <AppLayout>
                    <AppContent />
                  </AppLayout>
                </Router>
              </AlertProvider>
            </SafeAreaProvider>
          </ProductProvider>
        </BuyerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
